import { ToastType } from '@nx-frontend/shared/ui/toast';
import {
  DuplicateFolderState,
  DuplicateFolderStatus,
  UpdateDuplicateFolderStatePayload,
} from '@nx-frontend/shared/store/duplicate-folder';
import { getMenuTypeFromPositionType, prepareMenuItemsData } from '@nx-frontend/shared/store/feature-menu-tree';
import { Job } from '@nx-frontend/shared/store/jobs';
import {
  isFolderMenuItemType,
  MenuItem,
  MenuItemAddPayload,
  MenuItemDeletePayload,
  MenuItemType,
  MenuItemUpdatePayload,
  MenuType,
  NoteDto,
  PopupType,
  PortalServiceMenuItemDto,
  TranslatesFnType,
} from '@nx-frontend/shared/types';
import { OpenModalParams } from '@nx-frontend/shared/types';
import { AddNoteEventPayload, UpdateNoteEventPayload } from '@nx-frontend/shared/types';
import { MenuState } from '@nx-frontend/shared/types';
import { downloadFromIframe, isValidUrl } from '@nx-frontend/shared/utils';

export interface SidebarSocketEventsParams {
  workspaceId: string;
  userId: number;
  itemId?: string | null;
  pageType?: string | null;
  listenSocketEvent: <T>(eventName: string, listener: (data: SocketData<T>) => void) => void;
  webnotesMenuState: MenuState;
  addMenuItem: (payload: MenuItemAddPayload) => void;
  updateMenuItem: (payload: MenuItemUpdatePayload) => void;
  deleteMenuItem: (payload: MenuItemDeletePayload) => void;
  addNote: (payload: AddNoteEventPayload) => void;
  updateNote: (payload: UpdateNoteEventPayload) => void;
  deleteNote: (payload: UpdateNoteEventPayload) => void;
  getNotesNote: (payload: UpdateNoteEventPayload) => Promise<UpdateNoteEventPayload>;
  updateMenuItemParentSortOrder: (payload: { parentId: string; menuType: MenuType }) => void;
  toast: ({ ...props }: ToastType) => void;
  openModal: (modalState: OpenModalParams) => void;
  completeJob: (jobId: string) => void;
  getJob: (jobId: string) => Promise<Job>;
  getDuplicateFolderState: () => Promise<DuplicateFolderState>;
  resetDuplicateFolderState: () => void;
  updateDuplicateFolderState: (payload: UpdateDuplicateFolderStatePayload) => void;
  t?: TranslatesFnType;
}

let DUPLICATE_COMPLETED_TID: NodeJS.Timeout | number | null = null;

interface UpdateMenuItemParams {
  id?: string;
  type: string;
  parentId: string;
  menuState: MenuState;
  updatedAt?: number;
  createdAt?: number;
  order?: ['createdAt' | 'updatedAt', 'asc' | 'desc'];
}

interface SocketData<T> {
  message: T;
  workspaceId: string;
  userId: number;
}

/**
 * note:update message type
 */
interface UpdateEventMessage extends NoteDto {
  before: NoteDto;
  after: NoteDto;
  /**
   * fields that was changed for note
   */
  changedFields: string[];
}

/**
 * note:remove message type
 */
interface RemoveEventMessage {
  globalId: string;
  workspaceId: string;
  directlyRemovedNote: NoteDto;
}

/**
 * job:success message type
 */
interface JobSuccessEventMessage {
  globalId: string;
  workspaceId: string;
  taskType: string;
  taskData: unknown;
  uuid: string;
}

/**
 * job:error message type
 */
interface JobErrorEventMessage {
  globalId: string;
  workspaceId: string;
  taskType: string;
  taskData: unknown;
  uuid: string;
}

interface MoveNoteToWorkspaceTaskData {
  workspaceId: string;
  options: {
    remove: boolean;
  };
}

interface CopyMenuItemMessage {
  workspaceId: string;
  srcPortalId: string;
  srcMenuItemId: string;
  destinationPortalId: string;
  destinationMenuItemId: string;
  isSyncedCopy: boolean;
  menuItem: PortalServiceMenuItemDto;
  service: string;
}

export const updateMenuItemOrder = (params: UpdateMenuItemParams): { index: number } => {
  let index = 0;
  const { type, parentId, menuState } = params;

  const parentOrder = menuState?.entities[`${MenuItemType.NOTES_FOLDER}#${parentId}`]?.order || [];
  const folders = parentOrder?.filter((key: string) => key.startsWith(MenuItemType.NOTES_FOLDER + '#')) || [];
  const notes = parentOrder?.filter((key: string) => key.startsWith(MenuItemType.NOTE + '#')) || [];
  const firstFolderIndex = folders.length ? parentOrder.findIndex((key: string) => key === folders[0]) : 0;
  const lastFolderIndex = firstFolderIndex + folders.length;
  const firstNoteIndex = notes.length ? parentOrder.findIndex((key: string) => key === notes[0]) : 0;

  if (type === MenuItemType.NOTES_FOLDER) {
    index = lastFolderIndex;
  }

  if (type === MenuItemType.NOTE) {
    index = lastFolderIndex + firstNoteIndex + notes.length;
  }

  return { index };
};

export class SidebarSocketEvents {
  params: SidebarSocketEventsParams | null = null;
  inited = false;
  static instance: SidebarSocketEvents;

  static getInstance() {
    if (this.instance) {
      return this.instance;
    }
    this.instance = new SidebarSocketEvents();
    return this.instance;
  }

  init({
    listenSocketEvent,
    workspaceId,
    userId,
    itemId,
    pageType,
    webnotesMenuState,
    addMenuItem,
    updateMenuItem,
    deleteMenuItem,
    addNote,
    updateNote,
    deleteNote,
    getNotesNote,
    updateMenuItemParentSortOrder,
    toast,
    openModal,
    completeJob,
    getJob,
    getDuplicateFolderState,
    resetDuplicateFolderState,
    updateDuplicateFolderState,
    t,
  }: SidebarSocketEventsParams) {
    this.params = {
      itemId,
      pageType,
      workspaceId,
      userId,
      listenSocketEvent,
      webnotesMenuState,
      addMenuItem,
      updateMenuItem,
      deleteMenuItem,
      addNote,
      updateNote,
      deleteNote,
      getNotesNote,
      updateMenuItemParentSortOrder,
      toast,
      openModal,
      completeJob,
      getJob,
      getDuplicateFolderState,
      resetDuplicateFolderState,
      updateDuplicateFolderState,
      t,
    };

    if (!this.inited) {
      this.inited = true;
      this.listen();
    }
  }

  listen() {
    const { listenSocketEvent } = this.params || {};
    if (!listenSocketEvent) return;

    listenSocketEvent<UpdateEventMessage>('note:update', async ({ message, workspaceId }) => {
      const messageWorkspaceId = workspaceId;
      const {
        workspaceId: currentWorkspaceId,
        webnotesMenuState,
        addMenuItem,
        updateMenuItem,
        deleteMenuItem,
        addNote,
        updateNote,
        getNotesNote,
        updateMenuItemParentSortOrder,
      } = this.params || {};
      if (!webnotesMenuState || !addMenuItem || !updateMenuItem || !deleteMenuItem) return;

      // TODO: нужно создать стор меню в котором будут храниться деревью для всех воркспейсов
      // нужно слушать события для всех воркспейсов и соответственно обновлять стор заметок для всех воркспейсов

      const isBefore = !!Object.keys(message.before || {}).length;
      const isAfter = !!Object.keys(message.after || {}).length;

      const type = message.type === 'note' ? MenuItemType.NOTE : MenuItemType.NOTES_FOLDER;
      const id =
        type === MenuItemType.NOTE
          ? `${MenuItemType.NOTE}#${message.globalId}`
          : `${MenuItemType.NOTES_FOLDER}#${message.globalId}`;
      const icon = type === MenuItemType.NOTE ? 'page' : 'folder';

      const originalItem = webnotesMenuState.entities[id];
      // adding new item, or restore from trash
      if ((!isBefore && isAfter) || (isBefore && isAfter && message.before.parentId === 'trash')) {
        const parentId = `${MenuItemType.NOTES_FOLDER}#${message.parentId}`;
        const isFolder = type === MenuItemType.NOTES_FOLDER;

        // calculate position of item in menu tree
        // now items sorted only by createdAt/asc
        const { index } = updateMenuItemOrder({
          type,
          parentId: message.parentId,
          menuState: webnotesMenuState,
        });

        let is_portal_share = message.after?.isPortalShare ? message.after.isPortalShare : false;
        if (getNotesNote && !is_portal_share) {
          const note = await getNotesNote({ globalId: message.globalId, workspaceId: message.workspaceId });
          is_portal_share = is_portal_share ? is_portal_share : note?.isPortalShare ?? false;
        }

        const updatedAt = message.after.updatedAt ?? Math.floor(Date.now() / 1000);

        if (messageWorkspaceId === currentWorkspaceId && !is_portal_share) {
          addMenuItem({
            menuType: MenuType.WEBNOTES_MENU,
            item: {
              type,
              id,
              parentId,
              hasChildren: isFolder,
              icon,
              name: message.title,
              order: [],
              visible: true,
              targetId: '',
              index,
              url: '',
              slug: '',
              openInNewTab: false,
              createdAt: message.after?.createdAt,
              updatedAt: message.after?.updatedAt ?? updatedAt,
            },
          });

          if (updateMenuItemParentSortOrder) {
            // todo: обновлять сортировку у родительского элемента
            updateMenuItemParentSortOrder({ parentId, menuType: MenuType.WEBNOTES_MENU });
          }
        }

        // add note in workspace notes
        const { before, after, ...noteFields } = message;
        const { changedFields, ...note } = noteFields;
        const notePayload: AddNoteEventPayload = note;
        if (addNote && !is_portal_share) {
          addNote({
            ...notePayload,
            createdAt: after.createdAt,
            updatedAt: updatedAt,
          });
        }

        // update item that not in trash folder
      } else if (message.after.parentId !== 'trash') {
        const parentId = `${MenuItemType.NOTES_FOLDER}#${message.parentId}`;
        const updatedAt = message.after.updatedAt ?? Math.floor(Date.now() / 1000);

        const updatedItem: MenuItem = {
          ...originalItem,
          ...{
            parentId,
            name: message.title,
            isShared: message.shared,
          },
        };
        const updatedIcon = message.changedFields.includes('emoji')
          ? message.after.emoji
            ? message.after.emoji
            : icon
          : false;
        if (updatedIcon) {
          updatedItem.icon = updatedIcon;
          updatedItem.iconUrl = isValidUrl(updatedIcon) ? updatedIcon : undefined;
        }

        if (messageWorkspaceId === currentWorkspaceId) {
          // only update menu item if it was changed
          if (
            !originalItem ||
            updatedItem.parentId !== originalItem.parentId ||
            updatedItem.name !== originalItem.name ||
            updatedItem.isShared !== originalItem.isShared ||
            updatedItem.icon !== originalItem.icon
          ) {
            updateMenuItem({
              menuType: MenuType.WEBNOTES_MENU,
              item: updatedItem,
            });
          }
          if (updateMenuItemParentSortOrder) {
            // todo: обновлять сортировку у родительского элемента
            if (!originalItem || updatedItem.name !== originalItem.name) {
              // we are updating order only if name was changed
              updateMenuItemParentSortOrder({ parentId, menuType: MenuType.WEBNOTES_MENU });
            }
          }
        }

        // update note in workspace notes
        const { after, globalId, workspaceId } = message;
        const notePayload: UpdateNoteEventPayload = { ...after, globalId, workspaceId, updatedAt };
        updateNote && updateNote(notePayload);

        // delete item or move to trash
      } else if ((isBefore && !isAfter) || (isBefore && isAfter && message.after.parentId === 'trash')) {
        const updatedAt = message.after.updatedAt ?? Math.floor(Date.now() / 1000);

        if (messageWorkspaceId === currentWorkspaceId) {
          deleteMenuItem({
            menuType: MenuType.WEBNOTES_MENU,
            item: { ...originalItem },
          });

          const parentItem: MenuItem | undefined = webnotesMenuState?.entities[originalItem.parentId];
          if (parentItem) {
            updateMenuItem({
              menuType: MenuType.WEBNOTES_MENU,
              item: {
                ...parentItem,
                order: parentItem.order?.filter((key: string) => key !== id) ?? [],
                updatedAt,
              },
            });
          }
        }

        updateNote &&
          updateNote({
            globalId: message.globalId,
            workspaceId: message.workspaceId,
            parentId: 'trash',
            updatedAt,
          });
      }
    });

    listenSocketEvent<RemoveEventMessage>('note:remove', ({ message, workspaceId, userId }) => {
      const messageWorkspaceId = workspaceId;
      const { workspaceId: currentWorkspaceId, webnotesMenuState, deleteMenuItem, deleteNote } = this.params || {};

      if (webnotesMenuState && deleteMenuItem) {
        if (messageWorkspaceId === currentWorkspaceId) {
          const type = message.directlyRemovedNote.type;
          const id =
            type === MenuItemType.NOTE
              ? `${MenuItemType.NOTE}#${message.globalId}`
              : `${MenuItemType.NOTES_FOLDER}#${message.globalId}`;

          const originalItem = webnotesMenuState.entities[id];

          deleteMenuItem({ menuType: MenuType.WEBNOTES_MENU, item: { ...originalItem } });
        }
      }

      if (deleteNote) {
        deleteNote({ globalId: message.globalId, workspaceId: message.workspaceId });
      }
    });

    listenSocketEvent<JobSuccessEventMessage>('job:success', async ({ message, workspaceId, userId }) => {
      const {
        workspaceId: currentWorkspaceId,
        userId: currentUserId,
        toast,
        openModal,
        getJob,
        completeJob,
        getDuplicateFolderState,
        updateDuplicateFolderState,
        t,
      } = this.params || {};
      const { taskData, taskType } = message;
      // фильтр события перемещения/дублирования/копирования страницы
      if (taskType && taskData) {
        if (taskType === 'notes:moveNoteToWorkspace') {
          const moveNoteToWorkspaceTaskData = taskData as MoveNoteToWorkspaceTaskData;
          // фильтр по текущему воркспейсу и пользователю
          if (currentWorkspaceId === workspaceId && currentUserId === userId) {
            // пока будем считать за дублирование таску в которой страница не удаляется,
            // текущий воркспей равен тому куда была помещена страница
            let duplicateFolderState: DuplicateFolderState | null = null;
            if (getDuplicateFolderState) {
              duplicateFolderState = await getDuplicateFolderState();
            }
            if (
              workspaceId === moveNoteToWorkspaceTaskData.workspaceId &&
              moveNoteToWorkspaceTaskData.options.remove === false
            ) {
              if (duplicateFolderState?.jobId !== message.uuid) {
                toast?.({ title: t ? t('webnotes.webnotes-duplication_completed') : 'Duplication completed' });
              } else if (updateDuplicateFolderState) {
                duplicateFolderState.TID && clearTimeout(duplicateFolderState.TID);
                updateDuplicateFolderState({
                  status: DuplicateFolderStatus.COMPLETED,
                });
                DUPLICATE_COMPLETED_TID = setTimeout(() => {
                  updateDuplicateFolderState({
                    jobId: '',
                    taskId: '',
                    status: DuplicateFolderStatus.NONE,
                  });
                }, 10 * 1000);
                updateDuplicateFolderState({ TID: DUPLICATE_COMPLETED_TID });
              }
            } else if (workspaceId !== moveNoteToWorkspaceTaskData.workspaceId) {
              const isCopy = moveNoteToWorkspaceTaskData.options.remove !== true;
              openModal?.({
                content: PopupType.SUCCESS_MOVE_COPY,
                showCloseButton: true,
                title: t
                  ? t(isCopy ? 'webnotes.webnotes-copied_page_popup' : 'webnotes.webnotes-moved_page_popup')
                  : `Page successfully ${isCopy ? 'copied' : 'moved'}!`,
                closeButtonSize: 18,
                payload: {
                  payloadType: PopupType.SUCCESS_MOVE_COPY,
                  workspaceId: moveNoteToWorkspaceTaskData.workspaceId,
                  noteId: message.globalId,
                },
              });
            }
          }
        }
        if (taskType === 'editorServer:exportNote') {
          if (currentWorkspaceId === workspaceId && currentUserId === userId) {
            if ('fileUrl' in message && 'fileName' in message && 'uuid' in message) {
              if (
                typeof message.fileUrl === 'string' &&
                typeof message.fileName === 'string' &&
                typeof message.uuid === 'string'
              ) {
                if (getJob && completeJob) {
                  // find registered job, for not start download in onother browser tab
                  const job = await getJob(message.uuid);
                  if (job) {
                    completeJob(message.uuid);
                    downloadFromIframe(message?.fileUrl, message?.fileName);
                  }
                }
              }
            }
          }
        }
      }
    });

    listenSocketEvent<JobErrorEventMessage>('job:error', async ({ message, workspaceId, userId }) => {
      const {
        workspaceId: currentWorkspaceId,
        userId: currentUserId,
        toast,
        getDuplicateFolderState,
        resetDuplicateFolderState,
      } = this.params || {};
      const { taskData, taskType } = message;
      // фильтр события перемещения/дублирования/копирования страницы
      if (taskType && taskData) {
        if (taskType === 'notes:moveNoteToWorkspace') {
          const moveNoteToWorkspaceTaskData = taskData as MoveNoteToWorkspaceTaskData;
          // фильтр по текущему воркспейсу и пользователю
          if (currentWorkspaceId === workspaceId && currentUserId === userId) {
            // пока будем считать за дублирование таску в которой страница не удаляется,
            // текущий воркспей равен тому куда была помещена страница
            if (
              workspaceId === moveNoteToWorkspaceTaskData.workspaceId &&
              moveNoteToWorkspaceTaskData.options.remove === false
            ) {
              if (getDuplicateFolderState) {
                const duplicateFolderState = await getDuplicateFolderState();
                if (message.uuid === duplicateFolderState?.jobId) {
                  resetDuplicateFolderState && resetDuplicateFolderState();
                }
              }
              toast?.({ title: 'Duplicate error. Try again.' });
            } else if (workspaceId !== moveNoteToWorkspaceTaskData.workspaceId) {
              if (moveNoteToWorkspaceTaskData.options.remove === true) {
                toast?.({ title: 'Move error. Try again.' });
              } else {
                toast?.({ title: 'Copy error. Try again.' });
              }
            }
          }
        }
      }
    });

    listenSocketEvent<CopyMenuItemMessage>('portals:menuItemCopySuccess', async ({ message, workspaceId, userId }) => {
      const { workspaceId: currentWorkspaceId, addMenuItem, toast } = this.params || {};
      const { menuItem } = message;
      if (currentWorkspaceId === workspaceId) {
        const preparedMenuItemsData = prepareMenuItemsData([menuItem]);
        const menuType = menuItem.positionType ? getMenuTypeFromPositionType(menuItem.positionType) : undefined;
        if (menuType && addMenuItem) {
          Object.values(preparedMenuItemsData.items).forEach((item) => {
            addMenuItem({ menuType, item });
          });
          const isFolder = isFolderMenuItemType(menuItem as MenuItem);
          if (message.isSyncedCopy) {
            toast?.({ title: `The linked ${isFolder ? 'folder' : 'page'} has been successfully added to the portal` });
          }
        }
      }
    });
  }
}
